import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Send, Users, BarChart3, Paperclip, X, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const PTYPES = [['player_contacts', 'Player contacts'], ['staff', 'Staff'], ['both', 'Both']];
const METHODS = [['email', 'Email'], ['alert', 'Alert (app; email if no account)'], ['alert_email', 'Alert + Email']];

// §5.7 broadcast composer (program→division→team tree → families.contacts[]) +
// Sent list with per-message delivery telemetry and a per-recipient delivery log
// (C1). Alerts reach the app bell; people without an account get the email (C2).
// Attachments ≤ 20 MB and org-wide drafts (C4). Uses the /comm endpoints.
export default function CommBroadcastPanel({ orgId }) {
  const [tree, setTree] = useState([]);
  const [teamSel, setTeamSel] = useState({});
  const [ptype, setPtype] = useState('player_contacts');
  const [includeUnassigned, setIncludeUnassigned] = useState(false);
  const [resolved, setResolved] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [bcc, setBcc] = useState('');
  const [method, setMethod] = useState('email');
  const [atts, setAtts] = useState([]);
  const [draftId, setDraftId] = useState(null);
  const [sent, setSent] = useState([]);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [dFilter, setDFilter] = useState('');
  const [dSearch, setDSearch] = useState('');

  const load = useCallback(async () => {
    if (!orgId) return;
    try {
      const [t, m] = await Promise.all([
        axios.get(`${API}/api/comm/${orgId}/recipient-tree`),
        axios.get(`${API}/api/comm/${orgId}/messages`),
      ]);
      setTree(t.data.tree || []); setSent(m.data.messages || []);
    } catch (e) { console.error(e); }
  }, [orgId]);
  useEffect(() => { load(); }, [load]);

  const teamIds = () => Object.keys(teamSel).filter(k => teamSel[k]);
  const resolve = async () => {
    const { data } = await axios.post(`${API}/api/comm/${orgId}/resolve-recipients`,
      { team_ids: teamIds(), participant_type: ptype, include_unassigned: includeUnassigned });
    setResolved(data.recipients || []);
    return data.recipients || [];
  };
  const reset = () => { setSubject(''); setBody(''); setResolved(null); setAtts([]); setDraftId(null); setTeamSel({}); setBcc(''); };
  const send = async (status) => {
    const recips = resolved || await resolve();
    if (status === 'sent' && !window.confirm(`Send to ${recips.length} recipient(s)?`)) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/comm/${orgId}/compose`, {
        recipients: recips, subject, body, reply_to: replyTo, bcc, attachments: atts, draft_id: draftId,
        delivery_method: method, participant_type: ptype, team_ids: teamIds(), status,
      });
      reset(); load();
    } catch (e) { alert(e?.response?.data?.detail || 'Send failed'); } finally { setBusy(false); }
  };
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try { const { data } = await axios.post(`${API}/api/comm/${orgId}/attachments`, fd); setAtts(a => [...a, data.attachment]); }
    catch (e) { alert(e?.response?.data?.detail || 'Upload failed'); }
  };
  const open = async (m) => {
    const { data } = await axios.get(`${API}/api/comm/${orgId}/messages/${m.id}`);
    const msg = data.message;
    if (msg.status === 'draft') {
      setDraftId(msg.id); setSubject(msg.subject || ''); setBody(msg.body || ''); setMethod(msg.type || 'email');
      setReplyTo(msg.reply_to || ''); setBcc((msg.bcc || []).join(', ')); setAtts(msg.attachments || []);
      setPtype(msg.participant_type || 'player_contacts'); setResolved(msg.recipients || null);
      const sel = {}; (msg.team_ids || []).forEach(t => { sel[t] = true; }); setTeamSel(sel);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else { setDetail(msg); setDFilter(''); setDSearch(''); }
  };
  const delDraft = async (m) => {
    if (!window.confirm('Delete this draft?')) return;
    await axios.delete(`${API}/api/comm/${orgId}/messages/${m.id}`); load();
  };
  const fuzzy = (s, q) => { s = (s || '').toLowerCase(); let i = 0; for (const ch of q.toLowerCase()) { i = s.indexOf(ch, i); if (i < 0) return false; i++; } return true; };

  return (
    <div className="grid md:grid-cols-2 gap-4 mt-4" data-testid="comm-broadcast-panel">
      <div className="rounded-xl border p-4 bg-white dark:bg-slate-800 space-y-3">
        <h3 className="font-bold flex items-center gap-2"><Users className="h-4 w-4 text-teal-500" />Recipients</h3>
        <div className="max-h-52 overflow-y-auto space-y-2 text-sm">
          {tree.map(prog => (
            <div key={prog.program}>
              <div className="font-semibold">{prog.program}</div>
              {prog.divisions.map(d => (
                <div key={d.division} className="ml-3">
                  <div className="text-xs text-slate-400">{d.division}</div>
                  {d.teams.map(t => (
                    <label key={t.id} className="flex items-center gap-2 ml-3">
                      <input type="checkbox" checked={!!teamSel[t.id]} onChange={e => { setTeamSel(s => ({ ...s, [t.id]: e.target.checked })); setResolved(null); }} data-testid={`team-${t.id}`} />{t.name}
                    </label>
                  ))}
                </div>
              ))}
            </div>
          ))}
          {tree.length === 0 && <div className="text-slate-400">No teams yet.</div>}
        </div>
        <select value={ptype} onChange={e => { setPtype(e.target.value); setResolved(null); }} className="w-full rounded-md border p-2 text-sm dark:bg-slate-700 dark:border-slate-600" data-testid="ptype-select">
          {PTYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={includeUnassigned} onChange={e => { setIncludeUnassigned(e.target.checked); setResolved(null); }} data-testid="include-unassigned" /> Include unassigned participants</label>
        <button className="w-full px-3 py-2 border rounded text-sm" onClick={resolve} data-testid="resolve-btn">Resolve recipients</button>
        {resolved && <div className="text-xs text-slate-500" data-testid="resolved-count">{resolved.length} contact email(s) — one copy each, even if in several teams</div>}
      </div>

      <div className="rounded-xl border p-4 bg-white dark:bg-slate-800 space-y-2">
        <h3 className="font-bold flex items-center gap-2"><Send className="h-4 w-4 text-amber-500" />{draftId ? 'Edit draft' : 'Compose'}</h3>
        <div className="flex gap-2">
          <select value={method} onChange={e => setMethod(e.target.value)} className="rounded-md border p-2 text-sm dark:bg-slate-700 dark:border-slate-600" data-testid="method-select">
            {METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input placeholder="Reply-to" value={replyTo} onChange={e => setReplyTo(e.target.value)} className="flex-1 rounded-md border p-2 text-sm dark:bg-slate-700 dark:border-slate-600" data-testid="reply-to" />
        </div>
        <input placeholder="Subject" value={subject} onChange={e => setSubject(e.target.value)} className="w-full rounded-md border p-2 text-sm dark:bg-slate-700 dark:border-slate-600" data-testid="subject" />
        <textarea rows={5} placeholder="Message (HTML allowed)" value={body} onChange={e => setBody(e.target.value)} className="w-full rounded-md border p-2 text-sm dark:bg-slate-700 dark:border-slate-600" data-testid="body" />
        <input placeholder="BCC (comma-separated, ≤20)" value={bcc} onChange={e => setBcc(e.target.value)} className="w-full rounded-md border p-2 text-sm dark:bg-slate-700 dark:border-slate-600" data-testid="bcc" />
        <div className="text-sm space-y-1">
          <label className="inline-flex items-center gap-1 cursor-pointer text-amber-600"><Paperclip className="h-4 w-4" />Attach file (≤ 20 MB)
            <input type="file" className="hidden" onChange={e => { upload(e.target.files?.[0]); e.target.value = ''; }} data-testid="attach-input" /></label>
          {atts.map(a => <div key={a.id} className="flex items-center gap-2 text-xs">{a.name}<button onClick={() => setAtts(x => x.filter(y => y.id !== a.id))}><X className="h-3 w-3" /></button></div>)}
        </div>
        <div className="flex gap-2">
          {draftId && <button className="px-3 py-2 text-sm" onClick={reset}>New</button>}
          <button className="flex-1 px-3 py-2 border rounded text-sm" onClick={() => send('draft')} disabled={busy} data-testid="save-draft">Save draft</button>
          <button className="flex-1 px-3 py-2 rounded text-sm bg-amber-600 text-white" onClick={() => send('sent')} disabled={busy} data-testid="send-now">{busy ? <Loader2 className="h-4 w-4 animate-spin inline" /> : 'Send'}</button>
        </div>
      </div>

      <div className="md:col-span-2 rounded-xl border p-4 bg-white dark:bg-slate-800">
        <h3 className="font-bold flex items-center gap-2 mb-2"><BarChart3 className="h-4 w-4 text-purple-500" />Messages & drafts</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-700/40"><tr>
              <th className="p-2 text-left">Subject</th><th className="p-2">Type</th><th className="p-2">Status</th><th className="p-2">Recipients</th><th className="p-2">Delivered</th><th className="p-2">Failed</th><th className="p-2">Delivered %</th><th className="p-2"></th>
            </tr></thead>
            <tbody data-testid="sent-list">
              {sent.length === 0 && <tr><td colSpan={8} className="p-4 text-center text-slate-400">No broadcasts yet.</td></tr>}
              {sent.map(m => (
                <tr key={m.id} className="border-t dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/30 cursor-pointer" onClick={() => open(m)} data-testid={`msg-${m.id}`}>
                  <td className="p-2">{m.subject || '(no subject)'}<span className="block text-[10px] text-slate-400">{(m.sent_at || m.created_at || '').slice(0, 16).replace('T', ' ')} · {m.author_name || ''}</span></td>
                  <td className="p-2 text-center">{m.type}</td>
                  <td className="p-2 text-center">{m.status}</td>
                  <td className="p-2 text-center">{m.recipient_count}</td>
                  <td className="p-2 text-center text-green-600">{m.delivered_count ?? 0}</td>
                  <td className="p-2 text-center text-red-500">{m.failed_count ?? 0}</td>
                  <td className="p-2 text-center font-semibold">{m.delivered_pct ?? 0}%</td>
                  <td className="p-2 text-right">{m.status === 'draft' && <button onClick={e => { e.stopPropagation(); delDraft(m); }} className="text-slate-400 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={() => setDetail(null)} data-testid="msg-detail">
          <div className="h-full w-full max-w-xl overflow-y-auto bg-white dark:bg-slate-900 p-4 space-y-3" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center"><h3 className="font-bold">{detail.subject}</h3><button onClick={() => setDetail(null)}><X className="h-5 w-5" /></button></div>
            <div className="text-xs text-slate-400">{detail.type} · sent {(detail.sent_at || '').slice(0, 16).replace('T', ' ')} · {detail.recipient_count} recipients</div>
            <div className="flex gap-2">
              <select className="text-sm border rounded px-1 dark:bg-slate-800 dark:border-slate-600" value={dFilter} onChange={e => setDFilter(e.target.value)} data-testid="delivery-filter">
                <option value="">All</option><option value="delivered">Delivered</option><option value="failed">Failed</option>
              </select>
              <input className="flex-1 text-sm border rounded px-2 dark:bg-slate-800 dark:border-slate-600" placeholder="Search recipient" value={dSearch} onChange={e => setDSearch(e.target.value)} data-testid="delivery-search" />
            </div>
            <table className="w-full text-sm" data-testid="delivery-log">
              <thead><tr className="text-left text-slate-400"><th>Recipient</th><th>Channel</th><th>Status</th><th>Reason</th></tr></thead>
              <tbody>
                {(detail.deliveries || []).filter(d => (!dFilter || d.status === dFilter) && (!dSearch || fuzzy(d.email, dSearch))).map((d, i) => (
                  <tr key={i} className="border-t dark:border-slate-700"><td className="py-1">{d.email}</td><td>{d.channel}</td>
                    <td className={d.status === 'failed' ? 'text-red-500' : 'text-green-600'}>{d.status}</td><td className="text-xs text-slate-400">{d.error}</td></tr>
                ))}
                {!(detail.deliveries || []).length && <tr><td colSpan={4} className="py-3 text-slate-400">No per-recipient log for this older message.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
